import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import "./index.less";
import PopupBottomModal from "../popup-bottom-modal";
import { Toast, Loading } from "@xp-design/mobile";
import JSBridge from "@xpeng/js-bridge";
import XPMotorsApi from "@xpeng/xpmotors-jsbridge-api";
import { getClientEnv } from "../../utils/common";
import SharePosterPanel from "../share-poster-panel";
import wx_mini from "./assets/wx-mini.png";
import copy_link from "./assets/copy-link.png";
import generate_poster from "./assets/generate_poster.png";
import mini_h5_share_guide from "@/assets/mini_h5_share_guide.png";

import { urlToBase64 } from "@/utils/share";

type ShareInfo = {
  shareMainTitle: string;
  shareSubTitle: string;
  shareImgUrl: string;
  sharePageUrl?: string;
};

interface Props {
  title?: string;
  shareParms?: Record<string, string>;
  contentClassName?: string;
  btns?: string[];
  onClose: () => void;
  getPosterBase64Data?: () => Promise<string>;
  onClickWx?: () => void;
  onClickCopy?: () => void;
  onClickPoster?: () => void;
  canShare?: boolean;
}

export type SharePanelRef = {
  onOpen: (info: ShareInfo) => void;
  onClose: () => void;
};

const SharePanel = forwardRef<SharePanelRef, Props>(
  (
    {
      title = "分享至",
      contentClassName,
      btns,
      // shareParms,
      onClose,
      getPosterBase64Data = async () => "",
      onClickWx,
      onClickCopy,
      onClickPoster,
      canShare = true,
    },
    ref,
  ) => {
    // const { configPageQuery } = useConfigPage();
    const UA = navigator.userAgent || "";
    const isXPApp = /xiaopeng/i.test(UA); // 小鹏app
    const isWX = /micromessenger/i.test(UA); // 微信
    const isWXApp = isWX && /miniProgram/i.test(UA); // 微信小程序

    const [posterUrl, setPosterUrl] = useState("");
    const [showPosterPanel, setShowPosterPanel] = useState(false);
    const [showMiniShareGuideModal, setShowMiniShareGuideModal] =
      useState(false);
    const [visible, setVisible] = useState(false);
    const [shareInfo, setShareInfo] = useState<ShareInfo>({
      shareMainTitle: "",
      shareSubTitle: "",
      shareImgUrl: "",
      sharePageUrl: "",
    });

    const handleOpen = (info: ShareInfo) => {
      if (!canShare) {
        Toast.show({
          content: "该活动不允许分享",
          duration: 800,
        });
        return;
      }
      setShareInfo(info);
      setVisible(true);
    };

    const handleClose = () => {
      setVisible(false);
      onClose();
    };

    useImperativeHandle(ref, () => ({
      onOpen: handleOpen,
      onClose: handleClose,
    }));
    // const { sharePath } = useConfigPage();
    const copyToClipBoard = async () => {
      if (onClickCopy) {
        onClickCopy();
      }

      // const userUid = await getCurrentUid();
      // const path =
      //   sharePath ||
      //   `${window.location.origin}${window.location.pathname}#/configPage`;
      // const parms = {
      //   ...(shareParms || {}),
      //   shareUid: userUid,
      //   pageCode: configPageQuery?.data?.pageCode || "",
      // };

      const url = shareInfo?.sharePageUrl || "";

      console.log("复制url--> ", url);

      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);

      Toast.show({
        content: "已复制",
        duration: 800,
      });

      handleClose();
    };

    /**
     * 小鹏APP环境下，调用原生API分享小程序卡片给微信好友（注意：分享小程序卡片时，若host不是xiaopeng域名，点击无反应）
     * 微信小程序环境下，引导用户点击右上角胶囊按钮分享
     */
    const shareMiniProgram = async () => {
      if (onClickWx) {
        onClickWx();
      }

      // 不在APP环境下，不支持小程序分享
      // 若是微信小程序内，引导用户点击右上角胶囊按钮分享。其他环境内，toast提示在app内操作
      if (!isXPApp) {
        if (isWXApp) {
          setShowMiniShareGuideModal(true);
          handleClose();
        } else {
          Toast.show({
            content: "请在小鹏APP内进行操作",
            duration: 800,
          });
        }

        return;
      }

      try {
        Loading.show("加载中...");
        // thumb base64 转换
        let thumb = "";
        if (/^data:image\/(png|jpeg|jpg);base64,/.test(shareInfo.shareImgUrl)) {
          thumb = shareInfo.shareImgUrl.split(",")[1];
        } else if (shareInfo.shareImgUrl) {
          const base64 = (await urlToBase64(shareInfo.shareImgUrl)) as string;
          thumb = base64.split(",")[1];
        }
        console.log("thumb", thumb);

        let miniprogramType = 1; // 正式版:0，测试版:1，体验版:2
        if (getClientEnv() === "product") {
          miniprogramType = 0;
        }
        const callBridge = JSBridge.callHandler;
        callBridge.call(JSBridge, "shareMiniProgram", {
          webpageUrl: location.href,
          miniprogramType,
          userName: "gh_bcdeac2bff9d",
          path: `pages/webpage/index?pageUrl=${encodeURIComponent(shareInfo?.sharePageUrl || location.href)}`,
          title: shareInfo.shareMainTitle || "",
          desc: shareInfo.shareSubTitle || "",
          thumb: thumb, //thumbSizeKb && thumbSizeKb <= 128 ? thumb : '' // base64, 小于128k, 不需要base64类型说明头，只需要传原始图片数据
        });
      } catch (error) {
        Loading.hide();
        console.log("shareMiniProgram error-->", error);
      } finally {
        Loading.hide();
        handleClose();
      }
    };

    const showSharePosterPanel = () => {
      if (onClickPoster) {
        onClickPoster();
      }

      if (isXPApp) {
        showXpAppSharePoster();
      } else {
        showH5SharePoster();
      }

      handleClose();
    };

    const showXpAppSharePoster = async () => {
      if (!getPosterBase64Data) {
        console.error(
          "showXpAppSharePoster error: getPosterBase64Data is required",
        );
        return;
      }

      const imgData = await getPosterBase64Data();
      console.log("generatePoster url", imgData);

      // 获取图片类型
      const match = imgData.match(/data:image\/(.*?);base64,/);
      if (!match || match.length < 2) {
        console.error("generatePoster error: invalid base64 img data");
        return;
      }
      const imgType = match[1];

      setTimeout(() => {
        try {
          XPMotorsApi.sharePanel({
            style: imgData ? 4 : 1,
            addExtInfo: false,
            addUserInfo: false,
            addQrCode: false,
            data: {
              title: "",
              content: "",
              postImage: imgData
                ? imgData.replace(`data:image/${imgType};base64,`, "")
                : "",
              url: encodeURIComponent(window.location.href),
              shareType: 2,
            },
          } as any).then((res) => {
            console.log("XPMotorApi.sharePanel --> ", res);
          });
        } catch (error) {
          console.log("callShare error -->", error);
        }
      }, 0);
    };

    const showH5SharePoster = async () => {
      const imgData = await getPosterBase64Data();
      setPosterUrl(imgData);
      setShowPosterPanel(true);
    };

    /**
     * 默认展示的按钮code，不传默认展示微信好友和复制链接
     * wx: 微信好友
     * copy: 复制链接
     * poster: 生成海报
     */
    const defaultShowBtns = ["wx", "copy"];
    let showBtns = Array.isArray(btns) ? btns : defaultShowBtns;
    // 非小鹏app或非微信小程序环境下，不展示微信好友分享按钮
    if (!(isXPApp || isWXApp)) {
      showBtns = showBtns.filter((item) => item !== "wx");
    }

    return (
      <>
        <PopupBottomModal
          title={title}
          visible={visible}
          contentClassName={contentClassName}
          onClose={handleClose}
        >
          <div className="share-panel">
            {showBtns.includes("wx") && (
              <div className="item" onClick={shareMiniProgram}>
                <img className="icon" src={wx_mini} alt="" />
                <div className="name">微信好友</div>
              </div>
            )}
            {showBtns.includes("copy") && (
              <div className="item" onClick={copyToClipBoard}>
                <img className="icon" src={copy_link} alt="" />
                <div className="name">复制链接</div>
              </div>
            )}
            {showBtns.includes("poster") && (
              <div className="item" onClick={showSharePosterPanel}>
                <img className="icon" src={generate_poster} alt="" />
                <div className="name">生成海报</div>
              </div>
            )}
          </div>
        </PopupBottomModal>

        <SharePosterPanel
          posterUrl={posterUrl}
          visible={showPosterPanel}
          onClose={() => setShowPosterPanel(false)}
        />

        {showMiniShareGuideModal && (
          <div
            className="mini-share-guide-modal"
            onClick={() => setShowMiniShareGuideModal(false)}
          >
            <img alt="" className="share-guide-img" src={mini_h5_share_guide} />
          </div>
        )}
      </>
    );
  },
);

export default SharePanel;
